import { buildFiche, type AnalysisDocument, type DepositBackend, type Fiche, type FicheLine, type FicheSlide } from "@opulancia/deposit-core";
import { formatDate, h, plural, replace } from "../dom.js";

const BAND: Record<string, string> = { top: "haut", middle: "milieu", bottom: "bas" };
const TEXT_STATUS: Record<string, string> = { read: "texte lu", failed: "lecture du texte en échec", not_run: "texte non lu (OCR non exécuté)", unknown: "état de lecture inconnu" };

const swatch = (hex: string, label?: string) =>
  h("span", { class: "swatch", title: label ? `${hex} · ${label}` : hex }, h("span", { class: "swatch-dot", style: `background:${hex}` }), hex);

/**
 * The descriptive sheet (fiche descriptive), folded in the analysis section: loaded the first
 * time it is opened, from the stored analysis document. It shows what was read and measured,
 * with the analysis's own doubts; it says nothing of the story or the intent.
 */
export function ficheSection(backend: DepositBackend, depositId: string): HTMLElement | null {
  if (!backend.getAnalysisDocument) return null;
  const body = h("div", { class: "fiche-body" }, h("p", { class: "muted" }, "Chargement…"));
  let loaded = false;
  const details = h(
    "details",
    {
      class: "note fiche",
      "data-testid": "fiche",
      ontoggle: () => {
        if (loaded || !details.open) return;
        loaded = true;
        backend.getAnalysisDocument!(depositId)
          .then((doc) => replace(body, ...(doc ? render(buildFiche(doc)) : [h("p", { class: "muted" }, "Aucune analyse conservée.")])))
          .catch((error) => {
            loaded = false;
            replace(body, h("p", { class: "muted" }, `Fiche indisponible : ${error instanceof Error ? error.message : String(error)}`));
          });
      },
    },
    h("summary", null, "Fiche descriptive — texte et mesures, slide par slide"),
    body,
  );
  return details;
}

function render(f: Fiche): HTMLElement[] {
  const kinds = Object.entries(f.linesByKind)
    .map(([kind, n]) => `${n} ${kindLabel(kind)}`)
    .join(" · ");
  return [
    h(
      "p",
      { class: "muted fiche-intro" },
      `Calculée à partir de l'analyse conservée (version ${f.analyzerVersion}${f.generatedAt ? `, ${formatDate(f.generatedAt)}` : ""}). `,
      "Elle reprend ce qui a été lu et mesuré, avec les doutes de l'analyse ; elle n'interprète ni le récit ni l'intention. Les interprétations créatives restent celles des membres.",
    ),
    f.missing.length ? h("p", { class: "fiche-missing" }, `Cette version ne relève pas : ${f.missing.join(" ; ")}.`) : null,
    h(
      "dl",
      { class: "fiche-overview" },
      h("dt", null, "Slides"),
      h("dd", null, `${f.slides.length}${f.slides.some((s) => !s.analysed) ? `, dont ${f.slides.filter((s) => !s.analysed).length} non analysée(s)` : ""}`),
      h("dt", null, "Lignes lues"),
      h("dd", null, kinds || "aucune"),
      h("dt", null, "Numérotation lue"),
      h("dd", null, f.numbering.length ? f.numbering.map((n) => `slide ${n.position} : « ${n.text} »`).join(" · ") : "aucune"),
      h("dt", null, "Répété au même endroit"),
      h("dd", null, f.repeated.length ? h("ul", null, f.repeated.map((r) => h("li", null, `« ${r.text} » — slides ${r.positions.join(", ")}`))) : "rien"),
    ),
    ...f.slides.map(slide),
    h("details", { class: "note" }, h("summary", null, "Limites de ces mesures"), h("ul", null, f.limits.map((l) => h("li", null, l)))),
  ].filter((x): x is HTMLElement => x !== null);
}

function kindLabel(kind: string): string {
  return { editorial: "ajoutée(s) par-dessus", brand: "de marque", in_photo: "dans la photo", doubtful: "douteuse(s)" }[kind] ?? kind;
}

function slide(s: FicheSlide): HTMLElement {
  const title = `Slide ${s.position ?? "?"}`;
  const meta = [
    s.analysed ? TEXT_STATUS[s.textStatus] ?? s.textStatus : "non analysée",
    s.analysed && s.textStatus === "read" ? plural(s.lines.length, "ligne", "lignes") : null,
    s.uncertainties.length ? plural(s.uncertainties.length, "incertitude", "incertitudes") : null,
  ].filter(Boolean);
  return h(
    "details",
    { class: "fiche-slide", "data-testid": `fiche-slide-${s.position ?? "x"}` },
    h("summary", null, h("b", null, title), h("span", { class: "muted" }, ` — ${meta.join(" · ")}`)),
    !s.analysed ? h("p", { class: "muted" }, s.error ?? "Erreur inconnue.") : null,
    s.lines.length ? lines(s.lines) : null,
    s.analysed ? measures(s) : null,
    s.uncertainties.length ? h("div", { class: "fiche-doubts" }, h("b", null, "Incertitudes"), h("ul", null, s.uncertainties.map((u) => h("li", null, u)))) : null,
  );
}

function lines(ls: FicheLine[]): HTMLElement {
  return h(
    "div",
    { class: "table-scroll" },
    h(
      "table",
      { class: "fiche-lines" },
      h("thead", null, h("tr", null, h("th", null, "Texte lu"), h("th", null, "Catégorie"), h("th", null, "Hauteur dans l'image"), h("th", null, "Taille"), h("th", null, "Confiance"), h("th", null, "Couleurs"))),
      h(
        "tbody",
        null,
        ls.map((l) =>
          h(
            "tr",
            { class: l.flags.length ? "doubt" : null },
            h(
              "td",
              null,
              h("span", { class: "fiche-text" }, l.text || "—"),
              l.numbering ? h("span", { class: "chip" }, "numérotation") : null,
              (l.repeatedOn ?? 0) >= 2 ? h("span", { class: "chip" }, `répété sur ${l.repeatedOn} slides`) : null,
              l.flags.map((f) => h("span", { class: "chip warn" }, f)),
            ),
            h("td", null, l.kindLabel, l.uncertain && l.reasons.length ? h("div", { class: "muted fiche-reasons" }, l.reasons.join(" ; ")) : null),
            h("td", null, l.top !== null && l.bottom !== null ? `${l.top} – ${l.bottom} %` : "—", l.band ? h("div", { class: "muted" }, BAND[l.band] ?? l.band) : null),
            h("td", null, l.height !== null ? `${l.height} %` : "—"),
            h("td", null, l.score !== null ? `${Math.round(l.score * 100)} %` : "—"),
            h(
              "td",
              null,
              l.textHex ? h("div", null, h("span", { class: "muted" }, "lettres "), swatch(l.textHex)) : null,
              l.backgroundHex ? h("div", null, h("span", { class: "muted" }, "fond autour "), swatch(l.backgroundHex)) : null,
              l.coloured
                ? h("div", null, h("span", { class: "muted" }, l.coloured.role === "block" ? "bloc de couleur " : "lettres de couleur "), swatch(l.coloured.hex), l.coloured.insideHex ? [h("span", { class: "muted" }, " lettres dessus "), swatch(l.coloured.insideHex)] : null)
                : null,
              l.contrast !== null ? h("div", { class: "muted" }, `contraste ${l.contrast}`) : null,
            ),
          ),
        ),
      ),
    ),
  );
}

function measures(s: FicheSlide): HTMLElement {
  const comp = s.composition;
  return h(
    "div",
    { class: "fiche-measures" },
    h(
      "dl",
      { class: "fiche-overview" },
      s.size ? [h("dt", null, "Image"), h("dd", null, `${s.size[0]} × ${s.size[1]} px`)] : null,
      h("dt", null, "Composition"),
      h(
        "dd",
        null,
        !comp
          ? "non mesurée"
          : comp.status === "split_detected"
            ? `deux images ${comp.axis === "vertical" ? "côte à côte" : "l'une au-dessus de l'autre"}${comp.position !== null ? `, coupure à ${Math.round(comp.position * 100)} %` : ""}`
            : comp.status === "not_detected"
              ? "aucune coupure nette détectée"
              : comp.status,
      ),
      s.bottomMinusMiddle !== null ? [h("dt", null, "Bas moins milieu (luminosité)"), h("dd", null, String(s.bottomMinusMiddle))] : null,
      s.dominantColors.length ? [h("dt", null, "Couleurs dominantes"), h("dd", { class: "swatch-row" }, s.dominantColors.map((c) => swatch(c.hex, `${Math.round(c.share * 100)} %`)))] : null,
      s.ochreBlocks.length
        ? [h("dt", null, "Blocs ocre"), h("dd", null, h("ul", null, s.ochreBlocks.map((b) => h("li", null, swatch(b.hex), b.deltaE !== null ? ` (écart à l'ocre de référence ${b.deltaE})` : "", ` sur la ligne « ${b.text} »`))))]
        : null,
    ),
    s.detections.length
      ? h(
          "div",
          { class: "table-scroll" },
          h(
            "table",
            { class: "fiche-detections" },
            h("thead", null, h("tr", null, h("th", null, "Élément cherché"), h("th", null, "Comparé à la référence ZOO"), h("th", null, "Fiabilité"), h("th", null, "Mesure"))),
            h(
              "tbody",
              null,
              s.detections.map((d) =>
                h(
                  "tr",
                  { class: d.verdict === "uncertain" || d.verdict === "not_measured" ? "doubt" : null },
                  h("td", { title: d.statement }, d.label),
                  h("td", null, d.verdictLabel),
                  h("td", null, d.reliability ?? "—"),
                  h("td", null, [...d.facts, d.note].filter(Boolean).join(" ; ") || "—"),
                ),
              ),
            ),
          ),
        )
      : null,
  );
}

/** A FICTITIOUS analysis document for the demo (nothing is analysed in the browser). */
export function demoDocument(slides: Array<{ position: number | null }>, createdAt: string): AnalysisDocument {
  return {
    schema_version: "démo",
    generated_at: createdAt,
    analyzer: { version: "démo" },
    limits: ["DÉMO FICTIVE : aucune image n'est analysée dans le navigateur."],
    slides: slides.map((s, i) => ({
      position: s.position,
      role: "slide",
      status: "analyzed",
      text: {
        status: "read",
        lines: [
          { text: "OPULANCIA", kind: "brand", box: [0.37, 0.05, 0.62, 0.07], band: "top", score: 0.99, repeated_on: slides.length, kind_reasons: ["logo ou pseudo OPULANCIA lu"], coloured: null, colors: { text_hex: "#F6F2F0", background_hex: "#3F2F24", contrast_l: 74.8 } },
          ...(i === 0
            ? [
                { text: "TITRE FICTIF EN CAPITALES", kind: "editorial", box: [0.08, 0.7, 0.92, 0.78], band: "bottom", height_ratio: 0.07, score: 0.98, repeated_on: 1, coloured: null, colors: { text_hex: "#FFFFFF", background_hex: "#0B0A0A", contrast_l: 98 } },
                { text: "MOT EN OCRE", kind: "editorial", box: [0.2, 0.8, 0.8, 0.88], band: "bottom", height_ratio: 0.07, score: 0.97, repeated_on: 1, coloured: { role: "block", hex: "#DFA84D", inside_hex: "#0B0A0A" } },
              ]
            : []),
          ...(i === 1 ? [{ text: "ENSEIGNE (FICTIF)", kind: "in_photo", box: [0.2, 0.3, 0.4, 0.33], band: "middle", score: 0.62, low_confidence: true, kind_uncertain: true, kind_reasons: ["non : contraste ≥ 60"], repeated_on: 1, coloured: null }] : []),
        ],
      },
      measures: {
        image: { width: 1080, height: 1350 },
        composition: { status: "not_detected", axis: null, position: null },
        dominant_colors: [{ hex: "#E3DAD1", share: 0.4 }, { hex: "#261C12", share: 0.2 }],
        light: { bottom_minus_middle_l: -12 },
      },
      hypotheses: [
        { id: "zoo.logo_top", statement: "Logo OPULANCIA blanc, capitales espacées, centré en haut", verdict: "consistent", reliability: "high", evidence: { text: "OPULANCIA", similarity: 1 } },
        { id: "zoo.circled_arrow", statement: "Flèche dans un cercle ocre fin, en bas à droite", verdict: "uncertain", reliability: "low", note: "Aucun cercle détecté ; une absence n'est pas fiable avec cette méthode.", evidence: { circles: 0 } },
        ...(i === 0 ? [{ id: "zoo.ochre_blocks", statement: "Mots noirs sur rectangles ocre", verdict: "consistent", reliability: "medium", evidence: { blocks_in_lines: [{ text: "MOT EN OCRE", hex: "#DFA84D", delta_e: 0.9, inside_hex: "#0B0A0A" }] } }] : []),
      ],
    })),
  };
}
